import axios from 'axios';
import Store from 'store';
import Cookies from 'js-cookie';
import moment from 'moment';
import { navigateToUrl } from 'single-spa';

import { GOID_SERVICE } from '../api/constants';
import { delCurrentInventoryDropdownFromLocalStorage } from './currentInventoryDropdown';
import rewriteUrls from './interceptors/rewriteUrls';
import redirectLegacyApis from './interceptors/redirectLegacyApis';

const ACCESS_TOKEN = 'moka:accessToken';
const REFRESH_TOKEN = 'moka:refreshToken';
const ACCESS_TOKEN_EXPIRY = 'moka:accessTokenExpiry';
const AUTH_TYPE = {
  MOKA: 'MokaAuth-username',
  GO_AUTH: 'GoAuth-email',
  GO_ID: 'GoID-otp',
};

function getAuthAxiosInstance() {
  const instance = axios.create();

  instance.interceptors.request.use(redirectLegacyApis);
  instance.interceptors.request.use(rewriteUrls);

  return instance;
}

function invalidateSession() {
  // Old auth cookie
  Cookies.remove('remember_token', { path: '/', domain: '.mokapos.com' });

  // Needed for v1 logout
  Store.remove('response:user_role_name');

  // Auth data
  Store.remove('response:login');
  Store.remove('moka:authType');
  Store.remove(ACCESS_TOKEN);
  Store.remove(ACCESS_TOKEN_EXPIRY);
  Store.remove(REFRESH_TOKEN);

  delCurrentInventoryDropdownFromLocalStorage();

  // For GoFood Middleware UI
  Cookies.remove('moka:authType', { domain: '.mokapos.com' });
  Cookies.remove('moka:accessToken', { domain: '.mokapos.com' });
}

function sendToLogin() {
  invalidateSession();
  navigateToUrl('/login');
}

async function refreshMokaSession(refreshToken) {
  const { data } = await getAuthAxiosInstance().post(
    '/mkService/account/v2/token',
    null,
    {
      headers: {
        Authorization: refreshToken,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Content-Type': 'application/json',
      },
    }
  );

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { access_token, access_token_exp, refresh_token } = data;
  return {
    accessToken: access_token,
    refreshToken: refresh_token,
    expiry: access_token_exp,
  };
}

async function refreshGoAuthSession(refresToken) {
  const { data } = await getAuthAxiosInstance().post(
    `${GOID_SERVICE}/gobiz/goauth/oauth2/token`,
    {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      client_id: 'moka-pos-web',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      grant_type: 'refresh_token',
      // eslint-disable-next-line @typescript-eslint/naming-convention
      refresh_token: refresToken,
    },
    {
      headers: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'Authentication-Type': 'go-id',
      },
    }
  );

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { access_token, expires_in, refresh_token } = data;
  return {
    accessToken: access_token,
    refreshToken: refresh_token,
    expiry: moment().add(expires_in, 'seconds').toISOString(),
  };
}

export function getAuthType() {
  return Store.get('moka:authType') || AUTH_TYPE.MOKA;
}

export function getAccessToken() {
  return Store.get(ACCESS_TOKEN);
}

export function getRefreshToken() {
  return Store.get(REFRESH_TOKEN);
}

export function getTokenExpiry() {
  return Store.get(ACCESS_TOKEN_EXPIRY);
}

export function isAuthV2() {
  return !!getAccessToken() && !!getRefreshToken() && !!getTokenExpiry();
}

export async function refreshSession() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    sendToLogin();
    return;
  }

  try {
    const authType = getAuthType();
    let newSession;

    if (authType === AUTH_TYPE.MOKA) {
      newSession = await refreshMokaSession(refreshToken);
    } else if (authType === AUTH_TYPE.GO_AUTH) {
      newSession = await refreshGoAuthSession(refreshToken);
    } else {
      throw new Error('Existing auth type invalid');
    }

    Store.set(ACCESS_TOKEN, newSession.accessToken);
    Store.set(ACCESS_TOKEN_EXPIRY, newSession.expiry);
    Store.set(REFRESH_TOKEN, newSession.refreshToken);

    // BO-453 Exclusively for GoFood Middleware UI
    const tokenForGofood =
      authType === AUTH_TYPE.GO_AUTH
        ? `Bearer ${newSession.accessToken}`
        : newSession.accessToken;
    const expiryForGofood = new Date(newSession.expiry);
    const cookieSettingForGofood = {
      expires: expiryForGofood,
      domain: '.mokapos.com',
    };
    Cookies.set('moka:authType', authType, cookieSettingForGofood);
    Cookies.set('moka:accessToken', tokenForGofood, cookieSettingForGofood);
    Cookies.set('remember_token', tokenForGofood, cookieSettingForGofood);
  } catch (error) {
    // If failed to refresh session, redirect to login
    sendToLogin();
  }
}

/*
 * There are 4 generations of auth:
 * 1. `remember_token` cookie
 * 2. `moka:accessToken` in local storage (v1)
 * 3. `moka:accessToken`, `moka:refreshToken`, `moka:accessTokenExpiry` in local storage (v2)
 * 4. CMP
 */

export function isLoggedIn() {
  const userProfile = Store.get('response:login');
  const accessToken = getAccessToken();

  let isV2AuthValid = true;
  if (isAuthV2()) {
    const refreshToken = getRefreshToken();
    isV2AuthValid = !!refreshToken;
  }

  return !!userProfile && !!accessToken && isV2AuthValid;
}

export function isSessionExpired() {
  // v1 login never expires
  if (!isAuthV2()) {
    return false;
  }

  const expiry = Store.get(ACCESS_TOKEN_EXPIRY);
  return moment(expiry).isBefore(moment());
}

export async function login(email, password, phone) {
  const payload = phone
    ? // eslint-disable-next-line @typescript-eslint/naming-convention
      { session: { password, phone_number: phone } }
    : { session: { email, password } };

  return getAuthAxiosInstance().post('/mkService/account/v2/login', payload, {
    withCredentials: true,
  });
}

export async function migrateAuthToken() {
  const accessToken = getAccessToken();
  if (!accessToken) {
    sendToLogin();
    return;
  }

  try {
    const { data } = await getAuthAxiosInstance().post(
      '/mkService/account/v2/token/migrate',
      null,
      {
        headers: {
          Authorization: accessToken,
          // eslint-disable-next-line @typescript-eslint/naming-convention
          'Content-Type': 'application/json',
        },
      }
    );

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { access_token, access_token_exp, refresh_token } = data;
    Store.set(ACCESS_TOKEN, access_token);
    Store.set(ACCESS_TOKEN_EXPIRY, access_token_exp);
    Store.set(REFRESH_TOKEN, refresh_token);
  } catch (error) {
    // If failed to migrate session, redirect to login
    sendToLogin();
  }
}

export { AUTH_TYPE, invalidateSession };
